import {SketchDimension} from '../model/SketchDimension';
import type {
    SketchDimensionSketchSnapshot,
    SketchDimensionSnapshot,
} from '../SketchDimensionTypes';
import {SKETCH_DIMENSION_SNAPSHOT_SCHEMA_VERSION_V1} from '../SketchDimensionTypes';

/** 尺寸仓库变更事件类型。 */
export type SketchDimensionRepositoryEventType = 'created' | 'updated' | 'deleted' | 'restored';

/** 尺寸仓库变更事件。 */
export interface SketchDimensionRepositoryEvent {
    /** 事件类型。 */
    readonly type: SketchDimensionRepositoryEventType;
    /** 所属草图持久 ID。 */
    readonly sketchId: string;
    /** 尺寸持久 ID。 */
    readonly dimensionId: string;
}

/** 尺寸仓库事件监听器。 */
export type SketchDimensionRepositoryListener = (event: SketchDimensionRepositoryEvent) => void;

/**
 * 草图尺寸仓库。
 *
 * 设计说明：
 * 1. 仓库是尺寸聚合的唯一事实来源，按 sketchId 分桶管理；只保存纯领域聚合，
 *    不持有 Three.js、DOM、渲染或求解器对象；
 * 2. 所有变更走仓库方法并广播事件；`mutate` 在变更后校验草图内名称唯一性，
 *    失败自动恢复变更前快照，保证仓库永不处于非法状态；
 * 3. `toPersistentSnapshot()/fromPersistentSnapshot()` 是 `.puppy` 离线包与
 *    整表恢复的纯数据协议（02 计划 §9.1 预留 API）：dimensions 按 dimensionId
 *    字典序输出，保证确定性序列化；
 * 4. `restoreDimension/removeDimensionForRestore` 是历史 adapter 的恢复通道，
 *    按 dimensionId 幂等覆盖，不走用户操作校验。
 */
export class SketchDimensionRepository {
    /** 按草图分桶的尺寸聚合表。 */
    private readonly dimensionsBySketchId = new Map<string, Map<string, SketchDimension>>();

    /** 事件监听器集合。 */
    private readonly listeners = new Set<SketchDimensionRepositoryListener>();

    /**
     * 订阅仓库变更事件。
     *
     * @param listener 监听器。
     * @returns 取消订阅函数。
     */
    public observe(listener: SketchDimensionRepositoryListener): () => void {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    /** 广播事件。 */
    private emit(event: SketchDimensionRepositoryEvent): void {
        this.listeners.forEach((listener) => {
            listener(event);
        });
    }

    /** 取草图桶；不存在时可选创建。 */
    private bucketOf(sketchId: string, createIfMissing: boolean): Map<string, SketchDimension> | null {
        const existing = this.dimensionsBySketchId.get(sketchId);

        if (existing) {
            return existing;
        }
        if (!createIfMissing) {
            return null;
        }

        const bucket = new Map<string, SketchDimension>();

        this.dimensionsBySketchId.set(sketchId, bucket);

        return bucket;
    }

    /** 校验草图桶内名称唯一。 */
    private assertNameUnique(bucket: Map<string, SketchDimension>, name: string, selfId: string): void {
        for (const dimension of bucket.values()) {
            if (dimension.dimensionId !== selfId && dimension.currentName === name) {
                throw new Error(`草图内尺寸名称重复：${name}`);
            }
        }
    }

    /**
     * 创建尺寸。
     *
     * @param dimension 尺寸聚合。
     * @throws 当草图尺寸桶无法创建、尺寸 ID 已存在或尺寸名称与同草图其他尺寸重复时抛出错误。
     */
    public create(dimension: SketchDimension): void {
        const bucket = this.bucketOf(dimension.sketchId, true);

        if (!bucket) {
            throw new Error(`草图 ${dimension.sketchId} 尺寸桶创建失败。`);
        }
        if (bucket.has(dimension.dimensionId)) {
            throw new Error(`尺寸已存在：${dimension.dimensionId}`);
        }
        this.assertNameUnique(bucket, dimension.currentName, dimension.dimensionId);
        bucket.set(dimension.dimensionId, dimension);
        this.emit({type: 'created', sketchId: dimension.sketchId, dimensionId: dimension.dimensionId});
    }

    /**
     * 读取尺寸聚合。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @returns 尺寸聚合；不存在返回 null。
     */
    public get(sketchId: string, dimensionId: string): SketchDimension | null {
        return this.bucketOf(sketchId, false)?.get(dimensionId) ?? null;
    }

    /**
     * 断言尺寸存在并返回。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @returns 尺寸聚合。
     * @throws 当指定草图中不存在该尺寸时抛出错误。
     */
    public assert(sketchId: string, dimensionId: string): SketchDimension {
        const dimension = this.get(sketchId, dimensionId);

        if (!dimension) {
            throw new Error(`未找到尺寸：${dimensionId}（草图 ${sketchId}）`);
        }

        return dimension;
    }

    /**
     * 列出草图内全部尺寸（按 dimensionId 字典序）。
     *
     * @param sketchId 草图持久 ID。
     * @returns 尺寸聚合列表。
     */
    public listBySketch(sketchId: string): readonly SketchDimension[] {
        const bucket = this.bucketOf(sketchId, false);

        if (!bucket) {
            return [];
        }

        return [...bucket.values()]
            .sort((left, right) => left.dimensionId.localeCompare(right.dimensionId));
    }

    /**
     * 在受控上下文中变更尺寸。
     *
     * 变更后自动校验名称唯一性；mutator 抛错或校验失败时恢复变更前快照并重新抛出。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param mutator 变更函数。
     * @returns 变更后的快照。
     * @throws 当指定草图中不存在该尺寸时抛出错误。
     * @throws 当变更函数或名称唯一性校验失败时，恢复变更前快照并原样重新抛出原异常。
     */
    public mutate(
        sketchId: string,
        dimensionId: string,
        mutator: (dimension: SketchDimension) => void,
    ): SketchDimensionSnapshot {
        const bucket = this.bucketOf(sketchId, false);
        const dimension = bucket?.get(dimensionId);

        if (!bucket || !dimension) {
            throw new Error(`未找到尺寸：${dimensionId}（草图 ${sketchId}）`);
        }

        const before = dimension.toSnapshot();

        try {
            mutator(dimension);
            this.assertNameUnique(bucket, dimension.currentName, dimensionId);
        } catch (error) {
            bucket.set(dimensionId, SketchDimension.fromSnapshot(before));
            throw error;
        }
        this.emit({type: 'updated', sketchId, dimensionId});

        return dimension.toSnapshot();
    }

    /**
     * 删除尺寸。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @returns 被删除尺寸的快照。
     * @throws 当指定草图中不存在该尺寸时抛出错误。
     */
    public delete(sketchId: string, dimensionId: string): SketchDimensionSnapshot {
        const bucket = this.bucketOf(sketchId, false);
        const dimension = bucket?.get(dimensionId);

        if (!bucket || !dimension) {
            throw new Error(`未找到尺寸：${dimensionId}（草图 ${sketchId}）`);
        }

        const snapshot = dimension.toSnapshot();

        bucket.delete(dimensionId);
        if (bucket.size === 0) {
            this.dimensionsBySketchId.delete(sketchId);
        }
        this.emit({type: 'deleted', sketchId, dimensionId});

        return snapshot;
    }

    /**
     * 历史/持久化恢复通道：按快照幂等覆盖单条尺寸。
     *
     * @param snapshot 尺寸完整快照。
     * @throws 当草图尺寸桶无法创建，或恢复后名称与同草图其他尺寸重复时抛出错误。
     */
    public restoreDimension(snapshot: SketchDimensionSnapshot): void {
        const bucket = this.bucketOf(snapshot.sketchId, true);

        if (!bucket) {
            throw new Error(`草图 ${snapshot.sketchId} 尺寸桶创建失败。`);
        }

        const dimension = SketchDimension.fromSnapshot(snapshot);

        this.assertNameUnique(bucket, dimension.currentName, dimension.dimensionId);
        bucket.set(dimension.dimensionId, dimension);
        this.emit({type: 'restored', sketchId: snapshot.sketchId, dimensionId: snapshot.dimensionId});
    }

    /**
     * 历史恢复通道：删除单条尺寸（幂等，不存在时静默）。
     *
     * @param dimensionId 尺寸持久 ID。
     */
    public removeDimensionForRestore(dimensionId: string): void {
        for (const [sketchId, bucket] of this.dimensionsBySketchId) {
            if (!bucket.has(dimensionId)) {
                continue;
            }
            bucket.delete(dimensionId);
            if (bucket.size === 0) {
                this.dimensionsBySketchId.delete(sketchId);
            }
            this.emit({type: 'deleted', sketchId, dimensionId});

            return;
        }
    }

    /**
     * 输出单个草图的持久化快照（确定性：按 dimensionId 字典序）。
     *
     * @param sketchId 草图持久 ID。
     * @returns 版本化草图尺寸快照。
     */
    public toPersistentSnapshot(sketchId: string): SketchDimensionSketchSnapshot {
        return {
            schemaVersion: SKETCH_DIMENSION_SNAPSHOT_SCHEMA_VERSION_V1,
            sketchId,
            dimensions: this.listBySketch(sketchId).map((dimension) => dimension.toSnapshot()),
        };
    }

    /**
     * 从持久化快照整体恢复单个草图的尺寸（先清后灌）。
     *
     * @param snapshot 版本化草图尺寸快照。
     * @throws 当快照 schema 版本不受支持，或内部尺寸的 `sketchId` 与快照信封不一致时抛出错误。
     */
    public fromPersistentSnapshot(snapshot: SketchDimensionSketchSnapshot): void {
        if (snapshot.schemaVersion !== SKETCH_DIMENSION_SNAPSHOT_SCHEMA_VERSION_V1) {
            throw new Error(`不支持的尺寸快照 schema 版本：${String(snapshot.schemaVersion)}`);
        }
        this.deleteSketch(snapshot.sketchId);
        snapshot.dimensions.forEach((dimensionSnapshot) => {
            if (dimensionSnapshot.sketchId !== snapshot.sketchId) {
                throw new Error(
                    `尺寸 ${dimensionSnapshot.dimensionId} 的 sketchId ${dimensionSnapshot.sketchId} `
                    + `与快照信封 ${snapshot.sketchId} 不一致。`,
                );
            }
            this.restoreDimension(dimensionSnapshot);
        });
    }

    /**
     * 删除草图的全部尺寸（草图删除联动）。
     *
     * @param sketchId 草图持久 ID。
     */
    public deleteSketch(sketchId: string): void {
        const bucket = this.bucketOf(sketchId, false);

        if (!bucket) {
            return;
        }

        const dimensionIds = [...bucket.keys()];

        this.dimensionsBySketchId.delete(sketchId);
        dimensionIds.forEach((dimensionId) => {
            this.emit({type: 'deleted', sketchId, dimensionId});
        });
    }

    /**
     * 清空仓库（编辑器会话重置）。
     */
    public clear(): void {
        this.dimensionsBySketchId.clear();
    }
}
